/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useContext, useRef, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import io from 'socket.io-client';
import axios from 'axios';
import { CgAttachment } from 'react-icons/cg';
import { IoIosCloseCircle, IoIosChatbubbles, IoIosArrowBack } from 'react-icons/io';
import { FiInfo } from 'react-icons/fi';
import { Send } from 'lucide-react';
import { FaFilePdf, FaFileWord, FaFileExcel, FaFilePowerpoint, FaFileAlt } from 'react-icons/fa';
import { LoginContext } from '../../Contexts/LoginContext';
import { FloatingChatContext } from '../../Contexts/FloatingChatContext';
import { showToast } from '../../Components/utils/ShowToast';
import { formatDateTime } from '../../data/constants';
import SellerDefault from '../../images/pngs/seller.png';

const API = process.env.REACT_APP_Backend_Host_Name;

const FLOATING_CHAT_ROUTES = ['/productDetail/', '/seller/productDetail/', '/vendorDetail/', '/bookmarkSellers', '/orderDetailPage/', '/sellerOrderDetailPage/', '/checkoutPage'];

/** General first messages when messaging a seller (no listing context is attached to the chat). */
const SUGGESTED_MESSAGES_TO_SELLER = [
  'Hi, I would like to get in touch about your shop.',
  'Are you currently accepting new orders?',
  'Could you tell me more about what you offer?',
  'How do you usually handle shipping?',
];

/** First message ideas when the current user is messaging a buyer (e.g. seller replying). */
const SUGGESTED_MESSAGES_TO_BUYER = [
  'Hi! Thanks for reaching out.',
  'How can I help you today?',
  'Let me know if you have any questions.',
  'I will get back to you shortly.',
];

const renderDocumentIcon = (fileType) => {
  if (fileType?.startsWith('application/pdf')) return <FaFilePdf size={16} />;
  if (fileType?.includes('word')) return <FaFileWord size={16} />;
  if (fileType?.includes('excel') || fileType?.includes('spreadsheet')) return <FaFileExcel size={16} />;
  if (fileType?.includes('powerpoint') || fileType?.includes('presentation')) return <FaFilePowerpoint size={16} />;
  return <FaFileAlt size={16} />;
};

const renderTextWithLinks = (text) => {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, idx) => (
    /^https?:\/\/[^\s]+$/.test(part)
      ? <a key={`link-${idx}`} href={part} target="_blank" rel="noopener noreferrer">{part}</a>
      : <React.Fragment key={`txt-${idx}`}>{part}</React.Fragment>
  ));
};

const FloatingChatWidget = () => {
  const { user, isLogin } = useContext(LoginContext);
  const { isOpen, targetSellerId, closeFloatingChat, openFloatingChat, hideForSellerOwnProduct } = useContext(FloatingChatContext);
  const [location, setLocation] = useState(window.location.pathname);
  const token = localStorage.getItem('token');

  const [chats, setChats] = useState([]);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [sendLoading, setSendLoading] = useState(false);
  const [isChatsLoading, setIsChatsLoading] = useState(false);
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const [chatWarnings, setChatWarnings] = useState([]);
  const [activeWarning, setActiveWarning] = useState(null);
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const typingAnimationRef = useRef(null);

  const checkChatWarnings = useCallback((text) => {
    if (!text || !chatWarnings.length) return null;
    const lower = text.toLowerCase();
    for (const w of chatWarnings) {
      for (const word of w.words) {
        if (lower.includes(word.toLowerCase())) return w;
      }
    }
    return null;
  }, [chatWarnings]);

  const suggestedStarters = useMemo(() => {
    if (selectedParticipant?.role === 'buyer') return SUGGESTED_MESSAGES_TO_BUYER;
    return SUGGESTED_MESSAGES_TO_SELLER;
  }, [selectedParticipant?.role]);

  const clearTypingAnimation = useCallback(() => {
    if (typingAnimationRef.current != null) {
      clearInterval(typingAnimationRef.current);
      typingAnimationRef.current = null;
    }
  }, []);

  const animateMessageIntoInput = useCallback((fullText) => {
    if (!fullText) return;
    clearTypingAnimation();
    setMessage('');
    setActiveWarning(null);
    let i = 0;
    const stepMs = 32;
    typingAnimationRef.current = setInterval(() => {
      i += 1;
      const next = fullText.slice(0, i);
      setMessage(next);
      if (i >= fullText.length) {
        clearTypingAnimation();
        setActiveWarning(checkChatWarnings(fullText));
      }
    }, stepMs);
  }, [clearTypingAnimation, checkChatWarnings]);

  const showOnPage = FLOATING_CHAT_ROUTES.some(route => location.startsWith(route) || location === route);
  const isOnMainChatPage = location.startsWith('/chats');
  const shouldRender = isLogin && user && showOnPage && !isOnMainChatPage && !hideForSellerOwnProduct;

  useEffect(() => {
    const updateLocation = () => setLocation(window.location.pathname);
    const interval = setInterval(updateLocation, 200);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!isOpen) setSelectedParticipant(null);
  }, [isOpen]);


  useEffect(() => {
    if (targetSellerId && isOpen) {
      axios.get(`${API}/api/v1/auth/getUser/${targetSellerId}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          if (res.data.success) setSelectedParticipant(res.data.user);
        })
        .catch(() => showToast('Failed to load seller', 'error'));
    }
  }, [targetSellerId, isOpen, token]);

  useEffect(() => {
    if (!isOpen || !user) return;
    const fetchChatWarnings = async () => {
      try {
        const res = await axios.get(`${API}/api/v1/restrictions`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data.success && res.data.restriction?.chatWarnings) setChatWarnings(res.data.restriction.chatWarnings);
      } catch { }
    };
    fetchChatWarnings();
  }, [isOpen, user, token]);

  useEffect(() => {
    if (!isOpen || !user) return;
    socketRef.current = io(API, { auth: { token: localStorage.getItem('token') } });
    return () => {
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, [isOpen, user]);

  useEffect(() => {
    if (!socketRef.current || !selectedParticipant) return;
    const handler = (newMessage) => {
      if (selectedParticipant._id === newMessage.senderId) {
        setMessages(prev => [...prev, newMessage]);
      }
    };
    socketRef.current.on('receiveMessage', handler);
    return () => socketRef.current?.off('receiveMessage', handler);
  }, [selectedParticipant]);

  useEffect(() => {
    if (!isOpen || !user) return;
    setIsChatsLoading(true);
    axios.get(`${API}/api/v1/chats/userChats/${user._id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (res.data.success) {
          const userChats = (res.data.chats || []).filter(c => c.participantId && !c.isParticipantAdmin);
          setChats(userChats);
          if (targetSellerId && !selectedParticipant) {
            const existing = userChats.find(c => c.participantId?._id === targetSellerId);
            if (existing) setSelectedParticipant(existing.participantId);
          }
        }
      })
      .finally(() => setIsChatsLoading(false));
  }, [isOpen, user, token]);

  useEffect(() => {
    if (!selectedParticipant || !user) return;
    setIsMessagesLoading(true);
    axios.get(`${API}/api/v1/chats/userChats/${user._id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (res.data.success) {
          const chat = res.data.chats?.find(c => c.participantId?._id === selectedParticipant._id);
          setMessages(chat?.messages || []);
        }
      })
      .finally(() => setIsMessagesLoading(false));
  }, [selectedParticipant, user, token]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => () => clearTypingAnimation(), [clearTypingAnimation]);

  useEffect(() => {
    if (!isOpen) clearTypingAnimation();
  }, [isOpen, clearTypingAnimation]);

  useEffect(() => {
    clearTypingAnimation();
  }, [selectedParticipant?._id, clearTypingAnimation]);

  const sendMessage = async () => {
    if ((!message.trim() && !file) || !selectedParticipant || sendLoading) return;
    clearTypingAnimation();
    setSendLoading(true);
    const formData = new FormData();
    formData.append('senderId', user._id);
    formData.append('receiverId', selectedParticipant._id);
    formData.append('receiverEmail', selectedParticipant.email);
    formData.append('text', message);
    formData.append('isParticipantAdmin', 'false');
    if (file) formData.append('file', file);
    try {
      const res = await axios.post(`${API}/api/v1/chats/sendMessage/user`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.success) {
        setMessages(prev => [...prev, res.data.message]);
        setMessage('');
        setFile(null);
        setActiveWarning(null);
        socketRef.current?.emit('sendMessage', res.data.message);
      }
    } catch (err) {
      showToast(err?.response?.data?.error || 'Failed to send', 'error');
    } finally {
      setSendLoading(false);
    }
  };

  const handleFileChange = (e) => { setFile(e.target.files[0]); };
  const removeFile = () => { setFile(null); };

  const handleFileDownload = async (fileUrl, originalFileName) => {
    try {
      const res = await axios({
        url: `${API}/api/v1/chats/download`,
        method: 'GET',
        params: { fileUrl, originalFileName },
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = originalFileName || 'file';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch { showToast('Download failed', 'error'); }
  };

  if (!shouldRender) return null;

  return (
    <>
      <button
        className={`floatingChatButton ${isOpen ? 'active' : ''}`}
        onClick={() => isOpen ? closeFloatingChat() : openFloatingChat(null)}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        <IoIosChatbubbles className="floatingChatButtonIcon" />
      </button>

      {isOpen && (
        <div className="floatingChatOverlay" onClick={closeFloatingChat}>
          <div className="floatingChatPopup" onClick={e => e.stopPropagation()}>
            <div className="floatingChatHeader">
              <h3>Messages</h3>
              <button className="floatingChatCloseBtn" onClick={closeFloatingChat} aria-label="Close">
                <IoIosCloseCircle />
              </button>
            </div>

            <div className="floatingChatBody">
              {targetSellerId && !selectedParticipant ? (
                <div className="floatingChatLoading">Loading conversation...</div>
              ) : !selectedParticipant ? (
                <div className="floatingChatSelect">
                  {isChatsLoading ? (
                    <div className="floatingChatLoading">Loading...</div>
                  ) : chats.length > 0 ? (
                    <div className="floatingChatList">
                      {chats.map(chat => (
                        <div
                          key={chat.participantId?._id}
                          className="floatingChatListItem"
                          onClick={() => setSelectedParticipant(chat.participantId)}
                        >
                          <img src={chat.participantId?.sellerId?.profileImage || SellerDefault} alt="" />
                          <div>
                            <span className="fw600">{chat.participantId?.username}</span>
                            <p className="singleLineSmallText">
                              {chat.messages?.[chat.messages.length - 1]?.offer ? 'Custom offer' : chat.messages?.[chat.messages.length - 1]?.text || 'File'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="floatingChatEmpty">
                      <IoIosChatbubbles className="floatingChatEmptyIcon" />
                      <p>No conversations yet</p>
                      <span>Click &quot;Contact Seller&quot; on a product or seller profile to start chatting.</span>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="floatingChatRoomHeader">
                    {chats.length >= 1 && (
                      <button className="floatingChatBackBtn" onClick={() => setSelectedParticipant(null)} aria-label="Back to conversations">
                        <IoIosArrowBack size={22} />
                      </button>
                    )}
                    <img src={selectedParticipant?.sellerId?.profileImage || SellerDefault} alt="" />
                    <div>
                      {selectedParticipant?.role === 'seller' && selectedParticipant?.sellerId?._id ? (
                        <Link to={`/vendorDetail/${selectedParticipant.sellerId._id}`} className="floatingChatSellerName" onClick={closeFloatingChat}>
                          {selectedParticipant?.username}
                        </Link>
                      ) : (
                        <span className="floatingChatSellerName floatingChatBuyerName">{selectedParticipant?.username}</span>
                      )}
                      <span className="floatingChatViewFull">
                        <Link to={`/chats?p=${selectedParticipant._id}`} onClick={closeFloatingChat}>View full chat</Link>
                      </span>
                    </div>
                  </div>

                  <div className="floatingChatMessages">
                    {isMessagesLoading ? (
                      <div className="floatingChatLoading">Loading messages...</div>
                    ) : (
                      <>
                      {messages.map((msg, i) => (
                        msg.isOrderUpdate ? (
                          <div key={i} className="floatingChatOrderUpdate">
                            <FiInfo size={13} className="floatingChatOrderUpdateIcon" />
                            <span className="floatingChatOrderUpdateText">{msg.text}</span>
                            {msg.orderLink && (() => {
                              const orderIdFromLink = msg.orderLink.split('/').filter(Boolean).pop();
                              const viewOrderTo = orderIdFromLink
                                ? (user?.role === 'seller' ? `/sellerOrderDetailPage/${orderIdFromLink}` : `/orderDetailPage/${orderIdFromLink}`)
                                : msg.orderLink;
                              return <Link to={viewOrderTo} className="floatingChatOrderUpdateLink" onClick={closeFloatingChat}>View</Link>;
                            })()}
                          </div>
                        ) : (
                        <div key={i} className={`floatingChatMessage ${msg.senderId === user._id ? 'sent' : 'received'}`}>
                          <span className="floatingChatMessageTime">{formatDateTime(msg.timestamp)}</span>
                          {msg.offer ? (
                            <div className="floatingChatOfferPlaceholder">
                              <p>Custom offer received</p>
                              <Link to={`/chats?p=${selectedParticipant._id}`} onClick={closeFloatingChat}>View & respond in full chat</Link>
                            </div>
                          ) : msg.fileUrl ? (
                            <div className="floatingChatFile">
                              {msg.fileType?.startsWith('image/') && (
                                <img src={msg.fileUrl} alt="Attachment" style={{ maxWidth: 80, maxHeight: 80, borderRadius: 6 }} />
                              )}
                              {(msg.fileType?.startsWith('application/') || msg.fileType?.startsWith('text/')) && (
                                <div className="floatingChatDoc" onClick={() => handleFileDownload(msg.fileUrl, msg.originalFileName)}>
                                  {renderDocumentIcon(msg.fileType)}
                                  <span>{msg.originalFileName || 'File'}</span>
                                </div>
                              )}
                              {msg.text && <span>{renderTextWithLinks(msg.text)}</span>}
                            </div>
                          ) : (
                            <span>{renderTextWithLinks(msg.text)}</span>
                          )}
                        </div>
                        )
                      ))}
                      {messages.length === 0 && (
                        <div className="floatingChatSuggestedWrap" aria-live="polite">
                          <p className="floatingChatSuggestedLabel">Start the conversation</p>
                          <div className="floatingChatSuggestedList">
                            {suggestedStarters.map((text) => (
                              <button
                                key={text}
                                type="button"
                                className="floatingChatSuggestedChip"
                                onClick={() => animateMessageIntoInput(text)}
                              >
                                {text}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      </>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className="floatingChatInputArea">
                    {activeWarning && (
                      <div className="chatWarningBanner floatingChatWarning">
                        <FiInfo className="chatWarningIcon" />
                        <span>{activeWarning.message}</span>
                        <button className="chatWarningClose" onClick={() => setActiveWarning(null)}>&times;</button>
                      </div>
                    )}
                    {file && (
                      <div className="floatingChatFilePreview">
                        <span>{file.name}</span>
                        <button type="button" onClick={removeFile} className="floatingChatRemoveFile">&times;</button>
                      </div>
                    )}
                    <div className="floatingChatInputRow">
                      <input
                        type="text"
                        value={message}
                        onChange={e => {
                          clearTypingAnimation();
                          setMessage(e.target.value);
                          setActiveWarning(checkChatWarnings(e.target.value));
                        }}
                        placeholder="Type a message..."
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); sendMessage(); } }}
                        disabled={sendLoading}
                      />
                      <label className="floatingChatAttachBtn">
                        <CgAttachment size={20} />
                        <input type="file" onChange={handleFileChange} className="floatingChatFileInput" aria-label="Attach file" />
                      </label>
                      <button className="floatingChatSendBtn" onClick={sendMessage} disabled={sendLoading}>
                        <Send size={18} />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingChatWidget;
